/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, Inject} from '@angular/core';

import template from './creative-set-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {CreativeSetBulkActionModalComponent} from './creative-set-bulk-action-modal.component';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-creative-set-list-page',
  template
})
export class CreativeSetListPageComponent extends AdminResourceListPageBase {
  readonly itemStateName = 'app.adv.creative-sets.creative-set';
  readonly folioItemStateName = 'app.adv.folios.folio';
  readonly creativeItemStateName = 'app.adv.creatives.creative';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly userItemStateName = 'app.adm.users.user';

  readonly childColumns: AdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'layout', sortable: false},
    {param: 'width', label: 'dimensions', sortable: false},
    {param: '', label: 'info', sortable: false},
    {param: 'updateTime'},
    {param: '', label: 'targeting', sortable: false}
  ];

  private readonly fallbackColumns: AdminListColumn[] = [
    {param: 'name'},
    {param: 'userState'},
    {param: 'Folio', label: 'folio'},
    {param: 'startDate'},
    {param: 'endDate'},
    {param: 'createUser'},
    {param: 'updateTime'},
    {param: 'recentlyRequested'}
  ];

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(CreativeResource) private readonly Creative: any,
    private readonly angularModalService: AngularModalService
  ) {
    super(LocalNetworkProfile);
  }

  get visibleColumns(): AdminListColumn[] {
    return this.normalizeColumns(this.sortHeaderParams);
  }

  get sortHeaderParams(): any[] {
    if (this.list?.sel?.outputFields?.length) {
      return this.list.sel.outputFields;
    }
    return this.list?.defaultFields || this.fallbackColumns;
  }

  isFieldVisible(param: string): boolean {
    return this.isListFieldVisible(param, this.list?.defaultFields || this.fallbackColumns);
  }

  toggleChildren(creativeSet: any): void {
    this.list.getChildren?.(creativeSet);
  }

  childList(creativeSetId: string): any {
    return this.list.children?.[creativeSetId];
  }

  filtersResolved(): boolean {
    const list = this.list;
    if (!list) {
      return true;
    }
    return !!(list.selectInQuery?.$resolved
      && list.dateQuery?.$resolved
      && list.targetQuery?.$resolved
      && list.teamQuery?.$resolved);
  }

  userLinkDisabled(): boolean {
    const perms = this.list?.perms || {};
    return perms.MANAGE_SYSTEM !== true
      && perms.AX_ADVERTISER_ADMIN !== true
      && perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  layoutLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_LAYOUTS !== true;
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.list.canExpand = true;
    if (!this.list.getChildren) {
      getListChildren(this.list, this.adnListHelper, OBJECT_TYPE.CreativeSet, this.Creative);
    }
    if (!this.list.bulkAction) {
      this.list.bulkAction = () => {
        this.angularModalService.show(CreativeSetBulkActionModalComponent, {
          class: 'modal-lg',
          initialState: {list: this.list}
        });
      };
    }
  }
}
